/* eslint-disable indent */
import { html, css, LitElement } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { connect } from 'pwa-helpers/connect-mixin.js';
import { store } from '../../store/store.js';
import sharedStyles from '../../app/styles.js';
import {
    loadDeployments,
    loadToolPresets,
    saveDeployment,
    loadPlatformConfig,
    startRegistration,
    pollRegistration,
    clearRegistration,
    deploymentLabel,
    NEW_TOOL_ID,
} from '../../store/ltiAdvantage/deployments.js';
import { notify } from '../../store/notify/notify.js';
import { translate } from '../../helpers/translate.js';
import { applyStyles } from '../../helpers/stylesheets.js';
import { dialogFooterRenderer, dialogRenderer } from '@vaadin/dialog/lit.js';
import '@vaadin/dialog/theme/material/vaadin-dialog.js';
import '@vaadin/combo-box/theme/material/vaadin-combo-box.js';
import '@vaadin/context-menu/theme/material/vaadin-context-menu.js';
import '@sdb-develop/learning-frontend';
import '@polymer/paper-input/paper-input.js';
import '@polymer/iron-form/iron-form.js';
import '@polymer/paper-radio-button/paper-radio-button.js';
import '../shared/sdb-loading.js';

const modes = {
    LIST: 'list',
    CHOOSE: 'choose',       // how to add: register automatically or enter manually
    REGISTER: 'register',   // dynamic registration: url, popup, waiting for the supplier
    EDIT: 'edit',
    CONFIG: 'config',
};

// LTI Dynamic Registration §3.7: the tool posts this to its opener when it is done
const CLOSE_MESSAGE_SUBJECT = 'org.imsglobal.lti.close';
const POLL_INTERVAL_MS = 3000;
const POPUP_NAME = 'lti-registration';
const POPUP_FEATURES = 'width=1024,height=800';

const FORM_ID = 'lti13_deployment_form';

// Order and keys of the platform configuration shown to the supplier
const CONFIG_FIELDS = [
    'issuer',
    'clientId',
    'deploymentId',
    'authorizationEndpoint',
    'tokenEndpoint',
    'jwksUri',
    'openIdConfigurationUrl',
];

// Fields of a connection the admin edits; a chosen preset fills them, after which they are the connection's own
const URL_FIELDS = ['name', 'jwksUrl', 'loginUrl', 'redirectUrl', 'deepLinkingUrl'];

// How a manually added connection starts: from a known supplier (preset) or entered by the admin
const sources = {
    PRESET: 'preset',
    CUSTOM: 'custom',
};

/**
 * Manages the connections of the portal in the LTI Advantage Platform: list, add (prefilled from
 * a supplier preset, or from scratch), edit, and show the platform configuration a supplier needs.
 * A connection is self-contained; the preset is only a starting point and is never edited here.
 */
@customElement('sdb-lti-advantage-deployments-dialog')
class SdbLtiAdvantageDeploymentsDialog extends connect(store)(LitElement) {

    @state()
    accessor opened = false;

    @state()
    accessor mode = modes.LIST;

    @state()
    accessor deployments = [];

    @state()
    accessor presets = [];

    @state()
    accessor platformConfig;

    @state()
    accessor isSaving = false;

    @state()
    accessor isLoading = false;

    // presets or connections still loading: which presets are available is not known yet
    @state()
    accessor isLoadingPresets = false;

    // the connection being added/edited: { contextId?, toolId?, name, jwksUrl, loginUrl, redirectUrl, deepLinkingUrl, customParams }
    @state()
    accessor editing;

    // connection whose platform configuration is shown
    @state()
    accessor configDeployment;

    // message for a save the platform refused (e.g. 409: the portal already has this connection)
    @state()
    accessor saveError = null;

    // dynamic registration in progress (from the store) and the url typed for it
    @state()
    accessor registration = null;

    @state()
    accessor registrationUrl = '';

    @state()
    accessor popupBlocked = false;

    // opened straight on adding a connection (speed dial): cancelling the add flow closes the dialog
    _openedToAdd = false;

    // the source of the new connection was picked while presets were loading; pick again once they are known
    _sourceIsProvisional = false;

    // popup window, poll timer and message listener of the running registration
    _popup = null;
    _pollTimer = null;
    _onMessage = null;

    get form() {
        return document.getElementById(FORM_ID);
    }

    get isRegistrationBusy() {
        const status = this.registration?.status;
        return status === 'starting' || status === 'pending' || status === 'rejected';
    }

    get isNewDeployment() {
        return !this.editing?.contextId;
    }

    // A portal has one connection per preset, so presets already connected are not offered again.
    get availablePresets() {
        const connected = new Set(this.deployments.map((d) => d.toolId));

        return this.presets.filter((p) => !connected.has(p.toolId));
    }

    get heading() {
        switch (this.mode) {
            case modes.CHOOSE: return translate('addSupplierConnection');
            case modes.REGISTER: return translate('registerAutomatically');
            case modes.EDIT: return translate(this.isNewDeployment ? 'addDeployment' : 'editDeployment');
            case modes.CONFIG: return translate('platformConfig');
            default: return translate('supplierConnections');
        }
    }

    stateChanged(state) {
        const slice = state.ltiAdvantageDeployments || {};

        this.deployments = slice.list || [];
        this.presets = slice.presets || [];
        this.platformConfig = slice.platformConfig;
        this.isSaving = slice.status === 'saving';
        this.isLoading = slice.status === 'loading';
        this.isLoadingPresets = slice.presetsStatus === 'loading' || this.isLoading;
        this.registration = slice.registration;
    }

    willUpdate() {
        // presets arrived after "enter manually" was chosen: start from a supplier when there is one
        if (this._sourceIsProvisional && !this.isLoadingPresets) {
            this._sourceIsProvisional = false;
            if (this.mode === modes.EDIT && this.isNewDeployment) {
                this.editing = this._newConnection(this._defaultSource());
            }
        }
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this._stopRegistration();
    }

    static get styles() {
        return [sharedStyles, css``];
    }

    // The dialog body renders in the overlay outside this shadow root, so its styles are global.
    static get dialogStyles() {
        return css`
            .lti13-deployments {
                width: 680px;
                max-width: 90vw;
            }

            .lti13-deployments .row {
                display: flex;
                align-items: center;
                padding: 8px 0;
                border-bottom: 1px solid #e0e0e0;
            }

            .lti13-deployments .row .main {
                flex: 1;
                min-width: 0;
            }

            .lti13-deployments .title {
                font-weight: 500;
            }

            .lti13-deployments .sub {
                font-size: 13px;
                color: #888;
                overflow-wrap: anywhere;
            }

            .lti13-deployments .actions {
                padding-bottom: 12px;
                border-bottom: 1px solid #e0e0e0;
            }

            .lti13-deployments .rows {
                max-height: 60vh;
                overflow-y: auto;
            }

            .lti13-deployments .rows .row:last-child {
                border-bottom: none;
            }

            .lti13-deployments paper-input,
            .lti13-deployments vaadin-combo-box {
                width: 100%;
            }

            .lti13-deployments .hint {
                font-size: 12px;
                color: #888;
                margin: 8px 0 12px 0;
            }

            .lti13-deployments .error {
                font-size: 13px;
                color: var(--paper-input-container-invalid-color, #b00020);
                margin: 0 0 12px 0;
            }

            .lti13-deployments .choice {
                padding: 12px 16px;
                margin-bottom: 12px;
                border: 1px solid #e0e0e0;
                border-radius: 4px;
                cursor: pointer;
            }

            .lti13-deployments .choice:hover {
                background: #f5f5f5;
            }

            .lti13-deployments .sources {
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: 12px;
                margin-bottom: 8px;
            }

            .lti13-deployments .sources .choice {
                display: flex;
                align-items: flex-start;
                gap: 4px;
                margin-bottom: 0;
                padding: 12px 12px 12px 4px;
            }

            .lti13-deployments .sources .choice.selected {
                border-color: var(--btn-primary, var(--sdb-button-primary, #1976d2));
            }

            .lti13-deployments .sources .choice[disabled] {
                cursor: default;
                opacity: 0.6;
            }

            .lti13-deployments .sources .choice[disabled]:hover {
                background: none;
            }

            /* the tile handles the click; the radio only shows the state */
            .lti13-deployments .sources paper-radio-button {
                pointer-events: none;
                padding: 0 4px;
                --paper-radio-button-checked-color: var(--btn-primary, var(--sdb-button-primary, #1976d2));
                --paper-radio-button-checked-ink-color: var(--btn-primary, var(--sdb-button-primary, #1976d2));
            }

            /* The supplier dropdown's scroller is light DOM in the document: it overflows by a few
               pixels and showed a horizontal scrollbar under the items. Item text clips with an ellipsis. */
            vaadin-combo-box-scroller {
                overflow-x: hidden;
            }

            .lti13-deployments .waiting {
                display: flex;
                flex-direction: column;
                align-items: center;
                gap: 8px;
                padding: 16px 0;
                font-size: 13px;
                color: #666;
            }

            .lti13-deployments .config-row {
                display: flex;
                align-items: center;
                gap: 8px;
                padding: 6px 0;
                border-bottom: 1px solid #eee;
            }

            .lti13-deployments .config-row .label {
                width: 220px;
                flex-shrink: 0;
                font-size: 13px;
                color: #666;
            }

            .lti13-deployments .config-row code {
                flex: 1;
                font-size: 13px;
                overflow-wrap: anywhere;
            }
        `;
    }

    render() {
        return html`
            <vaadin-dialog
                id="lti13_deployments_dialog"
                overlay-class="app-theme-container"
                header-title=${this.heading}
                .opened=${this.opened}
                @opened-changed=${(e) => { this.opened = e.detail.value; }}
                ${dialogRenderer(() => this._renderBody(), [
                    this.opened,
                    this.mode,
                    this.deployments,
                    this.presets,
                    this.editing,
                    this.platformConfig,
                    this.configDeployment,
                    this.isLoading,
                    this.isLoadingPresets,
                    this.saveError,
                    this.registration,
                    this.registrationUrl,
                    this.popupBlocked,
                ])}
                ${dialogFooterRenderer(() => this._renderFooter(), [
                    this.opened,
                    this.mode,
                    this.isSaving,
                    this.registration,
                ])}>
            </vaadin-dialog>
        `;
    }

    firstUpdated() {
        applyStyles(SdbLtiAdvantageDeploymentsDialog.dialogStyles);
    }

    /**
     * Opens the list of connections, or with `add` straight on choosing how to add one.
     */
    open({ add = false } = {}) {
        this._openedToAdd = add;
        this.mode = add ? modes.CHOOSE : modes.LIST;
        this.editing = null;
        this.configDeployment = null;
        this.opened = true;

        store.dispatch(loadDeployments());
        store.dispatch(loadToolPresets());
    }

    close() {
        this._stopRegistration();
        this.opened = false;
    }

    _renderBody() {
        switch (this.mode) {
            case modes.CHOOSE: return this._renderChoose();
            case modes.REGISTER: return this._renderRegister();
            case modes.EDIT: return this._renderEdit();
            case modes.CONFIG: return this._renderConfig();
            default: return this._renderList();
        }
    }

    _renderChoose() {
        return html`
            <div class="lti13-deployments">
                <div class="choice" @click=${() => { this.mode = modes.REGISTER; }}>
                    <div class="title">${translate('registerAutomatically')}</div>
                    <div class="sub">${translate('registerAutomaticallyHint')}</div>
                </div>
                <div class="choice" @click=${() => this._edit(null)}>
                    <div class="title">${translate('enterManually')}</div>
                    <div class="sub">${translate('enterManuallyHint')}</div>
                </div>
            </div>
        `;
    }

    _renderRegister() {
        const registration = this.registration;
        const status = registration?.status;
        const finished = status === 'expired' || status === 'failed';

        return html`
            <iron-form id=${FORM_ID}>
                <form class="lti13-deployments">
                    <p class="hint">${translate('registerAutomaticallyIntro')}</p>
                    <paper-input
                        id="lti13_registration_url_input"
                        transparent
                        type="url"
                        label=${`${translate('toolRegistrationUrl')} *`}
                        auto-validate
                        required
                        error-message=${translate('errRequired')}
                        ?disabled=${this.isRegistrationBusy}
                        value=${this.registrationUrl}
                        @change=${(e) => { this.registrationUrl = e.target.value.trim(); }}>
                    </paper-input>

                    ${status === 'starting' || status === 'pending' ? html`
                        <div class="waiting">
                            <sdb-loading></sdb-loading>
                            <div>${translate('waitingForSupplier')}</div>
                            ${this.popupBlocked ? html`
                                <div class="error">
                                    ${translate('popupBlocked')}
                                    <a href=${registration.initiationUrl} target=${POPUP_NAME} rel="opener">${translate('openSupplierPage')}</a>
                                </div>
                            ` : ''}
                        </div>
                    ` : ''}

                    ${status === 'rejected' ? html`
                        <div class="error">${translate('registrationRejected')} ${registration.error ?? ''}</div>
                        <div class="hint">${translate('registrationRejectedHint')}</div>
                    ` : ''}

                    ${finished ? html`
                        <div class="error">${status === 'expired' ? translate('registrationExpired') : (registration.error ?? translate('errorOccurred'))}</div>
                    ` : ''}
                </form>
            </iron-form>
        `;
    }

    _renderList() {
        return html`
            <div class="lti13-deployments">
                <!-- the add action stays on top and visible; only the rows scroll -->
                <div class="actions">
                    <sdb-content-button
                        id="add_deployment_button"
                        label=${translate('addDeployment')}
                        outlined
                        @click=${() => { this.mode = modes.CHOOSE; }}>
                    </sdb-content-button>
                </div>
                <div class="rows">
                ${this.isLoading && this.deployments.length === 0
                    ? html`<sdb-loading></sdb-loading>`
                    : this.deployments.length === 0
                        ? html`<p>${translate('noDeploymentsForPortal')}</p>`
                        : this.deployments.map((deployment) => html`
                            <div class="row">
                                <div class="main">
                                    <div class="title">${deploymentLabel(deployment)}</div>
                                    <div class="sub">${deployment.isConfigured ? deployment.redirectUrl : translate('deploymentNotConfigured')}</div>
                                    <div class="sub">${deployment.resourceLinkCount} ${translate('resourceLinkCount')}</div>
                                </div>
                                <vaadin-context-menu
                                    open-on="click"
                                    .items=${[
                                        { text: translate('editDeployment'), action: () => this._edit(deployment) },
                                        { text: translate('platformConfig'), action: () => this._showConfig(deployment) },
                                    ]}
                                    @item-selected=${(e) => {
                                        e.stopPropagation();
                                        e.detail.value?.action?.();
                                    }}>
                                    <sdb-content-iconbutton icon="fal fa-ellipsis-vertical"></sdb-content-iconbutton>
                                </vaadin-context-menu>
                            </div>
                        `)
                }
                </div>
            </div>
        `;
    }

    _renderEdit() {
        const editing = this.editing;
        const setField = (field) => (e) => {
            this._sourceIsProvisional = false;
            this.editing = { ...this.editing, [field]: e.target.value.trim() || null };
        };
        // URLs are optional: suppliers often need the client id and deployment id before handing them out
        const urlInput = (field, required) => html`
            <paper-input
                id=${`lti13_${field}_input`}
                transparent
                type="url"
                label=${`${translate(field)}${required ? ' *' : ''}`}
                auto-validate
                ?required=${required}
                error-message=${translate('errRequired')}
                value=${editing[field] ?? ''}
                @change=${setField(field)}>
            </paper-input>
        `;

        return html`
            <iron-form id=${FORM_ID}>
                <form class="lti13-deployments">
                    <div class="error" ?hidden=${!this.saveError}>${this.saveError}</div>
                    ${this.isNewDeployment ? this._renderSource() : html`
                        <paper-input
                            id="lti13_name_input"
                            transparent
                            label=${`${translate('toolName')} *`}
                            auto-validate
                            required
                            error-message=${translate('errRequired')}
                            value=${editing.name ?? ''}
                            @change=${setField('name')}>
                        </paper-input>
                    `}

                    <!-- a preset shows its fields once one is picked -->
                    ${editing.source === sources.PRESET && !editing.toolId ? '' : html`
                        <!-- explains empty URLs while the connection is not configured yet; pointless once they are filled -->
                        <div class="hint" ?hidden=${editing.jwksUrl && editing.loginUrl && editing.redirectUrl}>${translate('urlsOptionalHint')}</div>
                        ${urlInput('jwksUrl', false)}
                        ${urlInput('loginUrl', false)}
                        ${urlInput('redirectUrl', false)}
                        ${urlInput('deepLinkingUrl', false)}
                    `}
                </form>
            </iron-form>
        `;
    }

    // A new connection starts from a known supplier (prefills the fields) or from details the admin enters.
    _renderSource() {
        const editing = this.editing;
        const hasPresets = this.availablePresets.length > 0;
        const presetHint = this.isLoadingPresets
            ? translate('loadingSuppliers')
            : translate(hasPresets ? 'selectSupplierHint' : 'noSuppliersAvailable');
        const sourceOption = (source, label, hint, disabled = false) => html`
            <div
                class="choice ${editing.source === source ? 'selected' : ''}"
                id=${`lti13_source_${source}`}
                ?disabled=${disabled}
                @click=${() => { if (!disabled) this._setSource(source); }}>
                <paper-radio-button .checked=${editing.source === source} ?disabled=${disabled}></paper-radio-button>
                <div>
                    <div class="title">${label}</div>
                    <div class="sub">${hint}</div>
                </div>
            </div>
        `;

        return html`
            <div class="sources">
                ${sourceOption(sources.PRESET, translate('selectSupplier'), presetHint, !hasPresets)}
                ${sourceOption(sources.CUSTOM, translate('enterYourself'), translate('enterYourselfHint'))}
            </div>

            ${editing.source === sources.PRESET ? html`
                <vaadin-combo-box
                    id="lti13_tool_combobox"
                    label=${translate('tool')}
                    item-value-path="toolId"
                    item-label-path="name"
                    clear-button-visible
                    required
                    error-message=${translate('errRequired')}
                    .items=${this.availablePresets}
                    .value=${editing.toolId ?? ''}
                    @value-changed=${(e) => this._selectPreset(e.detail.value)}>
                </vaadin-combo-box>
                <div class="hint" ?hidden=${!editing.toolId}>${translate('presetCopiedHint')}</div>
            ` : html`
                <paper-input
                    id="lti13_name_input"
                    transparent
                    label=${`${translate('toolName')} *`}
                    auto-validate
                    required
                    error-message=${translate('errRequired')}
                    value=${editing.name ?? ''}
                    @change=${(e) => {
                        this._sourceIsProvisional = false;
                        this.editing = { ...this.editing, name: e.target.value.trim() || null };
                    }}>
                </paper-input>
            `}
        `;
    }

    _renderConfig() {
        const deployment = this.configDeployment;
        const config = this.platformConfig;

        if (!deployment || !config || config.contextId !== deployment.contextId) {
            return html`<div class="lti13-deployments"><sdb-loading></sdb-loading></div>`;
        }

        return html`
            <div class="lti13-deployments">
                <div class="title">${deploymentLabel(deployment)}</div>
                <p class="hint">${translate('platformConfigIntro')}</p>
                <div class="actions">
                    <sdb-content-button
                        id="copy_config_button"
                        label=${translate('copyAll')}
                        outlined
                        @click=${() => this._copyConfig(deployment, config)}>
                    </sdb-content-button>
                </div>
                ${CONFIG_FIELDS.map((field) => html`
                    <div class="config-row">
                        <div class="label">${translate(field)}</div>
                        <code>${config[field]}</code>
                        <sdb-content-iconbutton
                            icon="fal fa-copy"
                            size="18"
                            title=${translate('copyToClipboard')}
                            @click=${() => this._copy(config[field])}>
                        </sdb-content-iconbutton>
                    </div>
                `)}
            </div>
        `;
    }

    _renderFooter() {
        switch (this.mode) {
            case modes.CHOOSE:
                return html`
                    <div class="buttons">
                        <sdb-content-button
                            label=${translate('cancel')}
                            outlined
                            @click=${() => this._cancelAdd()}>
                        </sdb-content-button>
                    </div>
                `;
            case modes.REGISTER: {
                const status = this.registration?.status;
                const canStart = !this.isRegistrationBusy || status === 'rejected';
                return html`
                    <div class="buttons">
                        <sdb-content-button
                            label=${translate('cancel')}
                            outlined
                            @click=${() => { this._stopRegistration(); this._cancelAdd(); }}>
                        </sdb-content-button>
                        <sdb-content-button
                            id="start_registration_button"
                            label=${translate(status === 'expired' || status === 'failed' || status === 'rejected' ? 'retry' : 'startRegistration')}
                            secondary
                            ?disabled=${!canStart}
                            @click=${() => this._startRegistration()}>
                        </sdb-content-button>
                    </div>
                `;
            }
            case modes.EDIT:
                return html`
                    <div class="buttons">
                        <sdb-content-button
                            label=${translate('cancel')}
                            outlined
                            @click=${() => { if (this.isNewDeployment) { this._cancelAdd(); } else { this.mode = modes.LIST; } }}>
                        </sdb-content-button>
                        <sdb-content-button
                            id="save_deployment_button"
                            label=${translate('save')}
                            secondary
                            ?disabled=${this.isSaving}
                            @click=${() => this._save()}>
                        </sdb-content-button>
                    </div>
                `;
            case modes.CONFIG:
                return html`
                    <div class="buttons">
                        <sdb-content-button
                            label=${translate('backToList')}
                            outlined
                            @click=${() => { this.mode = modes.LIST; }}>
                        </sdb-content-button>
                        <sdb-content-button
                            label=${translate('closeDialog')}
                            secondary
                            @click=${() => this.close()}>
                        </sdb-content-button>
                    </div>
                `;
            default:
                return html`
                    <div class="buttons">
                        <sdb-content-button
                            label=${translate('closeDialog')}
                            secondary
                            @click=${() => this.close()}>
                        </sdb-content-button>
                    </div>
                `;
        }
    }

    _edit(deployment) {
        this.editing = deployment
            ? {
                contextId: deployment.contextId,
                name: deployment.name,
                jwksUrl: deployment.jwksUrl,
                loginUrl: deployment.loginUrl,
                redirectUrl: deployment.redirectUrl,
                deepLinkingUrl: deployment.deepLinkingUrl,
                // not edited here, but sent back so an update does not clear them
                customParams: deployment.customParams,
            }
            : this._newConnection(this._defaultSource());
        this._sourceIsProvisional = !deployment && this.isLoadingPresets;
        this.saveError = null;
        this.mode = modes.EDIT;
    }

    // An empty new connection; with a custom source it is a new supplier the platform registers as a tool.
    _newConnection(source) {
        return {
            source,
            toolId: source === sources.CUSTOM ? NEW_TOOL_ID : null,
            ...Object.fromEntries(URL_FIELDS.map((field) => [field, null])),
            customParams: null,
        };
    }

    _defaultSource() {
        return this.availablePresets.length > 0 ? sources.PRESET : sources.CUSTOM;
    }

    // Leaves adding a connection: opened from the speed dial that closes the dialog, otherwise back to the list.
    _cancelAdd() {
        if (this._openedToAdd) {
            this.close();
        } else {
            this.mode = modes.LIST;
        }
    }

    _setSource(source) {
        this._sourceIsProvisional = false;
        if (this.editing?.source !== source) {
            this.editing = this._newConnection(source);
        }
    }

    // A preset fills the fields as a starting point; clearing the choice empties them again.
    _selectPreset(toolId) {
        if ((toolId || null) === (this.editing?.toolId || null)) {
            return;
        }

        const preset = this.presets.find((p) => p.toolId === toolId);
        if (!preset) {
            this.editing = this._newConnection(sources.PRESET);
            return;
        }

        const fields = Object.fromEntries(URL_FIELDS.map((field) => [field, preset[field] ?? null]));
        this.editing = { ...this.editing, toolId: preset.toolId, ...fields, customParams: preset.customParams ?? null };
    }

    _showConfig(deployment) {
        this.configDeployment = deployment;
        this.mode = modes.CONFIG;

        store.dispatch(loadPlatformConfig({ contextId: deployment.contextId }));
    }

    async _save() {
        if (this.form && !this.form.validate()) {
            return;
        }

        this.saveError = null;
        const result = await store.dispatch(saveDeployment({ deployment: this.editing }));

        if (saveDeployment.fulfilled.match(result)) {
            this._showConfig(result.payload);
            return;
        }

        // 409: the portal already has a connection for this preset (see PortalDeploymentService.CreateAsync)
        if (/\(409\)/.test(result.error?.message ?? '')) {
            this.saveError = translate('deploymentExists');
            store.dispatch(loadDeployments());
        }
    }

    /**
     * Starts a dynamic registration. The popup is opened synchronously in the click handler
     * (otherwise browsers block it) and pointed at the supplier's initiation url once the platform
     * has minted the token. Completion arrives as the LTI close message and, as a fallback for
     * browsers that block it, through polling the status.
     */
    async _startRegistration() {
        if (this.form && !this.form.validate()) {
            return;
        }

        this._stopRegistration();
        this.popupBlocked = false;
        this._popup = window.open('', POPUP_NAME, POPUP_FEATURES);
        if (!this._popup) {
            this.popupBlocked = true;
        }

        const result = await store.dispatch(startRegistration({ toolRegistrationUrl: this.registrationUrl }));
        if (!startRegistration.fulfilled.match(result)) {
            this._closePopup();
            return;
        }

        const { initiationId, initiationUrl } = result.payload;
        if (this._popup) {
            this._popup.location = initiationUrl;
        }

        this._onMessage = (event) => {
            let data = event.data;
            if (typeof data === 'string') {
                try { data = JSON.parse(data); } catch { return; }
            }
            if (data?.subject === CLOSE_MESSAGE_SUBJECT) {
                this._closePopup();
                this._poll(initiationId);
            }
        };
        window.addEventListener('message', this._onMessage);

        this._pollTimer = setInterval(() => {
            // the user closed the supplier's window: one last look, then stop waiting
            if (this._popup && this._popup.closed) {
                this._popup = null;
                this._poll(initiationId, { final: true });
                return;
            }
            this._poll(initiationId);
        }, POLL_INTERVAL_MS);
    }

    async _poll(initiationId, { final = false } = {}) {
        const result = await store.dispatch(pollRegistration({ initiationId }));
        if (!pollRegistration.fulfilled.match(result)) {
            return;
        }

        const { status, deployment } = result.payload;
        if (status === 'completed') {
            this._stopRegistration({ keepResult: true });
            store.dispatch(loadDeployments());
            store.dispatch(notify({ text: translate('deploymentSaved') }));
            if (deployment) {
                this._showConfig(deployment);
            } else {
                this.mode = modes.LIST;
            }
            return;
        }

        if (status === 'expired' || final) {
            // stop waiting; the form shows the outcome and offers a retry
            this._stopRegistration({ keepResult: true });
        }
    }

    _stopRegistration({ keepResult = false } = {}) {
        if (this._pollTimer) {
            clearInterval(this._pollTimer);
            this._pollTimer = null;
        }
        if (this._onMessage) {
            window.removeEventListener('message', this._onMessage);
            this._onMessage = null;
        }
        this._closePopup();
        if (!keepResult) {
            store.dispatch(clearRegistration());
        }
    }

    _closePopup() {
        if (this._popup && !this._popup.closed) {
            this._popup.close();
        }
        this._popup = null;
    }

    // Copies the whole panel as "label: value" lines, ready to paste into a mail to the supplier.
    _copyConfig(deployment, config) {
        const lines = [
            deploymentLabel(deployment),
            ...CONFIG_FIELDS.map((field) => `${translate(field)}: ${config[field] ?? ''}`),
        ];

        return this._copy(lines.join('\n'));
    }

    async _copy(value) {
        try {
            await navigator.clipboard.writeText(value ?? '');
            store.dispatch(notify({ text: translate('copied') }));
        } catch (error) {
            console.error('Copy to clipboard failed', error);
        }
    }
}
